import { z } from "zod";

export type EnergyLabelMetadata = z.infer<
  typeof energyLabelResponseSchema
>["responseMetaData"];
export type EnergyLabelResults = z.infer<
  typeof energyLabelResponseSchema
>["results"];
export type EnergyLabelResult = z.infer<typeof energyLabelResultSchema>;
export type EnergyLabelProductFoundResult = z.infer<
  typeof energyLabelProductFoundResultSchema
>;
export type EnergyLabelMetaAverageUsage = z.infer<
  typeof energyLabelMetaAverageUsage
>;
export type AlternativeProduct = z.infer<typeof alternativeProductSchema>;

const alternativeProductSchema = z.object({
  manufacturer: z.string(),
  sku: z.string(),
  imageUrl: z.string(),
  ekoScore: z.number(),
  price: z.string(),
  priceUnformatted: z.number().nullable(),
  url: z.string(),
  energyCost: z.string(),
  energyCostSaving: z.string(),
  compareRun: z.string(),
  compareBuy: z.string(),
  compareSaving: z.string(),
  isSponsored: z.boolean(),
  badge: z.string().nullable(),
  ekoGroupValue: z.string().or(z.number()).nullable(),
  isPriceFromPartner: z.boolean(),
});

const energyLabelProductFoundResultSchema = z.object({
  productFound: z.literal(true),
  mostEfficientEnergyCost: z.string(),
  mostEfficientEkoScore: z.number(),
  ekoScore: z.number(),
  leastEfficientEnergyCost: z.string(),
  leastEfficientEkoScore: z.number(),
  energyCostSavingUnformatted: z.number().nullable(),
  energyCostSaving: z.string(),
  energyCost: z.string(),
  ekoGroupValueRatingTextPlural: z.string(),
  medalRatingText: z.string(),
  alternativeProducts: z.array(alternativeProductSchema),
  calculationData: z.object({
    operationalPowerConsumption: z.string(),
    energyCostByYear: z.record(z.string(), z.string()),
    totalEnergyCost: z.string(),
  }),
  manufacturer: z.string(),
  sku: z.string(),
});

const energyLabelResultSchema = z.object({
  productFound: z.boolean(),
  manufacturer: z.string().optional(),
  sku: z.string().optional(),
  imageUrl: z.string().optional(),
  productType: z.string().optional(),
  hasEnergyData: z.boolean().optional(),
  energyCost: z.string().optional(),
  energyCostUnformatted: z.number().nullable().optional(),
  mostEfficientEnergyCost: z.string().optional(),
  mostEfficientEkoScore: z.number().optional(),
  leastEfficientEnergyCost: z.string().optional(),
  leastEfficientEkoScore: z.number().optional(),
  maximumSaving: z.string().optional(),
  ekoScore: z.number().optional(),
  price: z.string().optional(),
  priceUnformatted: z.number().nullable().optional(),
  isPriceFromPartner: z.boolean().optional(),
  ekoGroupValueText: z.string().optional(),
  ekoGroupValueRatingText: z.string().optional(),
  ekoGroupValueTextPlural: z.string().optional(),
  ekoGroupValueRatingTextPlural: z.string().optional(),
  comparedEnergyCostUnformatted: z.number().nullable().optional(),
  comparedEnergyCost: z.string().optional(),
  comparedEkoScore: z.number().optional(),
  energyCostSavingUnformatted: z.number().nullable().optional(),
  energyCostSaving: z.string().optional(),
  averageEnergyCost: z.string().optional(),
  averageEnergyCostUnformatted: z.number().nullable().optional(),
  averageEkoScore: z.number().optional(),
  averageEnergyCostSaving: z.string().optional(),
  averageEnergyCostSavingUnformatted: z.number().nullable().optional(),
  alternativeProducts: z.array(alternativeProductSchema).optional(),
  numberOfAlternatives: z.number().optional(),
  medalRatingText: z.string().optional(),
  calculationData: z
    .object({
      operationalPowerConsumption: z.string(),
      energyCostByYear: z.record(z.string(), z.string()),
      totalEnergyCost: z.string(),
    })
    .optional(),
});

const energyLabelMetaAverageUsage = z.object({
  averageUsage: z.number().nullable(),
  averageUsagePeriod: z.number().nullable(),
  averageUsageTimesPerPeriod: z.number().nullable(),
  energyCostYears: z.number(),
});

const energyLabelMetadataSchema = z.object({
  general: z.object({
    calculationData: z.object({
      annualCostPerKwhPercentageChange: z.string(),
      annualCostPerKwhPercentageChangeNumberOfYears: z.string(),
      tariffByYear: z
        .array(z.string().optional())
        .or(z.record(z.string(), z.string())),
    }),
    productTypes: z
      .array(z.string().optional())
      .or(z.record(z.string(), energyLabelMetaAverageUsage)),
    tariff: z.number(),
  }),
});

export const energyLabelResponseSchema = z.object({
  status: z.object({
    code: z.number(),
    message: z.string(),
  }),
  responseMetaData: energyLabelMetadataSchema,
  results: z.record(z.string(), energyLabelResultSchema),
});
